import React, { useEffect, useState } from 'react';
import { axisBottom, axisLeft } from 'd3-axis';
import { scaleOrdinal } from 'd3-scale';
import { select, mouse } from 'd3-selection';
import styles from './dmarc-chart.module.scss';
import PropTypes from 'prop-types';
import { buildYScale, buildSizeScale } from './dmarc-chart-helpers';
import DmarcChartTooltip from './dmarc-chart-tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import { buildChartConfig } from './dmarc-chart-config';
const mapFillColor = ( row ) => {
    return styles[row.colorClass]; //CSS Class Name
};

const renderXAxis = ( xAxisEl, config ) => {
    const { width, chartRightBorder,  xAxisTickStart, xAxisTickEnd } = config;

    const xAxisComp = select( xAxisEl )
        .attr( 'width', width )
        .attr( 'class', styles.xAxis );

    const xAxis = axisBottom()
        .scale( scaleOrdinal()
            .domain( ['Low', 'DMARC Pass Rate', 'High'] )
            .range( [xAxisTickStart, ( xAxisTickEnd - xAxisTickStart ) / 2 ,xAxisTickEnd] ) )
        .tickSizeOuter( 0 );

    xAxisComp.call( xAxis );

    // insets the beginning and end tick labels to fit within the ticks
    xAxisComp.selectAll( '.tick:first-of-type text' )
        .attr( 'x', 10 );
    xAxisComp.selectAll( '.tick:last-of-type text' )
        .attr( 'x', -12 );

    // Don't show middle tick
    xAxisComp.select( '.tick:nth-child(3) line' ).remove();
    // extends path line past 0 and 100 percent
    xAxisComp.select( 'path.domain' )
        .attr( 'd', () => {
            return 'M0,0.5V0.5H' + ( chartRightBorder );
        } );
};

const renderYAxis = ( yAxisEl, config ) => {
    const { height, width } = config;

    const yScale = buildYScale( height );
    const yAxisComp = select( yAxisEl )
        .attr( 'width', width )
        .attr( 'height', height )
        .attr( 'class', styles.yAxis );

    const yAxis = axisLeft()
        .scale( yScale )
        .tickSize( width - 50 );

    yAxisComp.call( yAxis );

    // position the text of ticks below the line
    yAxisComp.selectAll( '.tick text' )
        .attr( 'x', 0 )
        .attr( 'y', 8 );
};


const renderBubbles = ( bubblesEl, aggDomainsData, setTooltip ) => {
    let sizeScale = buildSizeScale( aggDomainsData );
    select( bubblesEl ).selectAll( '*' ).remove(); //Clear previous bubbles if any

    return select( bubblesEl ).selectAll( 'dot' )
        .data( aggDomainsData )
        .enter()
        .append( 'circle' )
        .attr( 'cx', function( d ){
            return d.x;
        } )
        .attr( 'cy', function( d ){
            return d.y;
        } )
        .attr( 'r', function( row ){
            return sizeScale(  row.totalMailCount );
        } )
        .attr( 'class', ( row ) => {
            return `${mapFillColor( row )}`;
        } )
        .on( 'mouseleave', function( row ){
            setTooltip( false );
        } )
        .on( 'mousemove', function( row ){
            let coords = mouse( this );
            setTooltip( {
                data: row,
                x: coords[0],
                y: coords[1],
            } );
        } )
        .on( 'mouseenter', function( row ){
            setTooltip( {
                data: row,
                x: row.x,
                y: row.y,
            } );
        } );
};
let xAxisEl = React.createRef();
let yAxisEl = React.createRef();
let bubblesEl = React.createRef();


// Initialize webworker for chart processing
let bubbleWorker = false;
const DmarcChart = ( { aggDomainsData = [] } ) => {
    let chartConfig = buildChartConfig();
    const posBottomOfChart = `translate(${chartConfig.chartRightBorderOffset}, ${chartConfig.height - chartConfig.xAxisHeight})`;
    const posRightOfChart = `translate(${chartConfig.chartRightBorder + chartConfig.chartRightBorderOffset}, 0)`;

    const [tooltip, setTooltip] = useState( false );
    const [simulated, setSimulated] = useState( [] );


    // Only re-render axes when relevant config changes
    useEffect( () => {
        renderXAxis( xAxisEl, chartConfig );
        renderYAxis( yAxisEl, chartConfig );
    }, [chartConfig] );


    // When we get new data, send it to the bubbleWorker for processing
    useEffect( () => {
        let simulationConfig = {
            height: chartConfig.height,
            width: chartConfig.width,

        };

        if( bubbleWorker ){
            // Cancel any in-progress calculation if our data changes
            bubbleWorker.terminate();
        }
        // Register listener for bubbleWorker responses
        bubbleWorker = new Worker( '/static/js/workers/BubbleChart.worker.bundle.js' );
        bubbleWorker.onmessage = function( event ){
            setSimulated( event.data );
        };

        setSimulated( [] );
        bubbleWorker.postMessage( {
            aggDomainsData,
            config: simulationConfig,
        } );
    }, [aggDomainsData, chartConfig.height, chartConfig.width] );

    // When simulated data changes, use it to render the bubbles
    useEffect( () => {
        renderBubbles( bubblesEl, simulated, setTooltip );
    }, [simulated] );


    return (
        <svg
            data-automation-id="domain-portfolio-dmarc-chart"
            preserveAspectRatio="xMidYMid meet"
            viewBox={`0 0 ${chartConfig.width} ${chartConfig.height}`}
            width={`${chartConfig.width}`}
            height={`${chartConfig.height}`}
        >
            <g ref={el => xAxisEl = el}
                transform={posBottomOfChart} />
            <g ref={el => yAxisEl = el}
                transform={posRightOfChart} />
            <g data-rendered={simulated.length > 0} className={tooltip.data ? styles['tooltip-open'] : styles['tooltip-closed']} ref={el => bubblesEl = el} />
            <g>
                { tooltip.data && (
                    <DmarcChartTooltip
                        maxX={chartConfig.width}
                        maxY={chartConfig.height}
                        mouseX={tooltip.x}
                        mouseY={tooltip.y}
                        data={tooltip.data}
                    />
                ) }

            </g>
            <g>
                {simulated.length === 0 && (
                    // Loading Overlay
                    <foreignObject
                        width={chartConfig.width}
                        height={chartConfig.height}
                        className={styles.chartLoader}
                    >
                        <CircularProgress />
                    </foreignObject>
                )}
            </g>
        </svg>
    );
};


DmarcChart.propTypes = {
    aggDomainsData: PropTypes.array,
};

export default( DmarcChart );
